import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isPrerenderCrawl } from './isPrerenderCrawl';
import {
    PRIVACY_EVENT,
    GAME_COMPLETED_EVENT,
    getPrivacyStatus,
    hasCompletedGame,
    isAnalyticsOff,
    submitBirthYear,
    declineAnalytics,
} from './privacy';
import './AgeGate.css';

// Not shown to test runners or the prerender crawler, and never on the legal
// pages themselves (people need to be able to read them before answering).
const shouldSkip = (path) => {
    if (typeof window === 'undefined') return true;
    if (window.navigator.webdriver || isPrerenderCrawl()) return true;
    return path === '/privacy' || path === '/terms';
};

// Any of the game's popups (rules, stats, end of game, ...) being open.
const MODAL_SELECTOR =
    '.modal-overlay, .rules-modal-overlay, .glossary-modal-overlay, .stats-modal-overlay';
const modalOpen = () => !!document.querySelector(MODAL_SELECTOR);

// Neutral age prompt that also serves as the analytics notice. Playing never
// depends on answering: "No thanks" (or ignoring it) just means anonymous
// counts only, with no persistent ID and no session replay.
// Wording deliberately doesn't hint at an age threshold.
//
// It appears on the home page right away (no popups compete there), and
// elsewhere only after the visitor has finished a game and only while no
// other popup is open, so it never stacks on the first-visit rules popup or
// covers the end-of-game buttons.
function AgeGate() {
    const [visible, setVisible] = useState(false);
    const [year, setYear] = useState('');
    const { pathname } = useLocation();

    useEffect(() => {
        const sync = () => setVisible(
            !shouldSkip(pathname) &&
            !isAnalyticsOff() &&
            getPrivacyStatus() === null &&
            (pathname === '/' || hasCompletedGame()) &&
            !modalOpen()
        );
        sync();
        window.addEventListener(PRIVACY_EVENT, sync);
        window.addEventListener(GAME_COMPLETED_EVENT, sync);
        // Popups mount/unmount in the DOM, so re-check when it changes.
        const observer = new MutationObserver(sync);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => {
            window.removeEventListener(PRIVACY_EVENT, sync);
            window.removeEventListener(GAME_COMPLETED_EVENT, sync);
            observer.disconnect();
        };
    }, [pathname]);

    if (!visible) return null;

    const thisYear = new Date().getFullYear();
    const years = [];
    for (let y = thisYear; y >= thisYear - 100; y--) years.push(y);

    return (
        <div className="age-gate" role="dialog" aria-label="Privacy and analytics">
            <p className="age-gate-text">
                We count plays anonymously. Share your birth year to also let us record sessions and
                collect usage data to improve the game.{' '}
                <Link to="/privacy">Privacy Policy</Link>
            </p>
            <div className="age-gate-controls">
                <select aria-label="Birth year" value={year} onChange={(e) => setYear(e.target.value)}>
                    <option value="">Birth year</option>
                    {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
                <button
                    type="button"
                    className="age-gate-continue"
                    disabled={year === ''}
                    onClick={() => submitBirthYear(year)}
                >
                    Continue
                </button>
                <button type="button" className="age-gate-decline" onClick={() => declineAnalytics()}>
                    No thanks
                </button>
            </div>
        </div>
    );
}

export default AgeGate;
